// frontend/src/pages/DashboardPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDashboard } from '../hooks/useDashboard';
import { MetricCard } from '../components/dashboard/MetricCard';
import { RecentSalesTable } from '../components/dashboard/RecentSalesTable';
import { TopPerformersRanking } from '../components/dashboard/TopPerformersRanking';
import { ChevronRight, ShoppingBag, AlertCircle } from 'lucide-react';
import paymentService from '../services/paymentService';
import { formatCurrency } from '../utils/format';
import GlobalMetricsModal from '../components/dashboard/GlobalMetricsModal';
import { CashCutModal } from '../components/sales/CashCutModal';
import { DashboardTimer } from '../components/dashboard/DashboardTimer';
import { OperationalExcelTable } from '../components/dashboard/OperationalExcelTable';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    metrics,
    recentSales,
    revenueComparison,
    loading,
    error,
    refresh
  } = useDashboard();

  const [topVendedoras, setTopVendedoras] = useState<any[]>([]);
  const [topModistas, setTopModistas] = useState<any[]>([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
  const [showCashCutModal, setShowCashCutModal] = useState(false);

  useEffect(() => {
    loadRankings();
  }, []);

  const loadRankings = async () => {
    try {
      setRankingLoading(true);
      const now = new Date();
      const inicio = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const fin = now.toISOString().split('T')[0];

      const vendedorasData = await paymentService.calcularComisionesVendedoras(inicio, fin);
      const modistasData = await paymentService.calcularComisionesModistas(inicio, fin);

      setTopVendedoras(vendedorasData.vendedoras || []);
      setTopModistas(modistasData.modistas || []);
    } catch (error) {
      console.error('Error loading rankings:', error);
    } finally {
      setRankingLoading(false);
    }
  };

  // SVG Icons
  const SalesIcon = (
    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
  const CashIcon = (
    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  const BoxIcon = (
    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
  const ClipboardIcon = (
    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-red-800 font-medium">{error}</p>
          </div>
          <button onClick={refresh} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium">
            Reintentar
          </button>
        </div>
      </Layout>
    );
  }

  const monthName = new Date().toLocaleString('es-MX', { month: 'long', year: 'numeric' });
  const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);

  // Concentrado items config
  const saleIconSvg = (size: string) => (
    <svg className={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  const lockIconSvg = (size: string) => (
    <svg className={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
  const clockIconSvg = (size: string) => (
    <svg className={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  const checkIconSvg = (size: string) => (
    <svg className={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  const alertIconSvg = (size: string) => (
    <svg className={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
    </svg>
  );

  const concentradoItems = [
    {
      label: 'Vendido del Mes',
      value: metrics?.totalRevenue || 0,
      sublabel: 'Mes actual',
      bg: 'bg-green-50', border: 'border-green-200',
      textColor: 'text-green-700', textBold: 'text-green-900',
      iconColor: 'text-green-600', iconWatermark: 'text-green-200',
      icon: saleIconSvg('w-4 h-4'), iconLarge: saleIconSvg('w-full h-full'),
      onClick: () => navigate('/ventas')
    },
    {
      label: 'En Apartados',
      value: metrics?.totalApartados || 0,
      sublabel: 'Mes actual',
      bg: 'bg-purple-50', border: 'border-purple-200',
      textColor: 'text-purple-700', textBold: 'text-purple-900',
      iconColor: 'text-purple-600', iconWatermark: 'text-purple-200',
      icon: lockIconSvg('w-4 h-4'), iconLarge: lockIconSvg('w-full h-full'),
      onClick: () => navigate('/apartados')
    },
    {
      label: 'En Pre-Orders',
      value: metrics?.totalPreOrders || 0,
      sublabel: 'Mes actual',
      bg: 'bg-yellow-50', border: 'border-yellow-200',
      textColor: 'text-yellow-700', textBold: 'text-yellow-900',
      iconColor: 'text-yellow-600', iconWatermark: 'text-yellow-200',
      icon: clockIconSvg('w-4 h-4'), iconLarge: clockIconSvg('w-full h-full'),
      onClick: () => navigate('/pre-orders')
    },
    {
      label: 'Cobrado del Mes',
      value: metrics?.totalCobrado || 0,
      sublabel: 'Pagos del mes',
      bg: 'bg-blue-50', border: 'border-blue-200',
      textColor: 'text-blue-700', textBold: 'text-blue-900',
      iconColor: 'text-blue-600', iconWatermark: 'text-blue-200',
      icon: checkIconSvg('w-4 h-4'), iconLarge: checkIconSvg('w-full h-full'),
      onClick: () => setShowCashCutModal(true)
    },
    {
      label: 'Pendiente',
      value: metrics?.totalPendiente || 0,
      sublabel: 'Del mes',
      bg: 'bg-red-50', border: 'border-red-200',
      textColor: 'text-red-700', textBold: 'text-red-900',
      iconColor: 'text-red-500', iconWatermark: 'text-red-200',
      icon: alertIconSvg('w-4 h-4'), iconLarge: alertIconSvg('w-full h-full'),
      onClick: () => navigate('/ventas?status=PENDIENTE')
    }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Widget de Apartados y Pre-orders Pendientes */}
        {metrics && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between border-l-4 border-l-purple-500">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                      <ShoppingBag className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Apartados / Pre-orders pendientes</h3>
                  </div>
                  <button 
                    onClick={() => navigate('/apartados')}
                    className="text-purple-600 font-bold text-sm hover:text-purple-700 flex items-center gap-1 group transition-colors"
                  >
                    Ver todo <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div 
                    onClick={() => navigate('/apartados?status=APARTADO')}
                    className="p-4 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors group"
                  >
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 group-hover:text-purple-500 transition-colors">Apartados</p>
                    <p className="text-2xl font-black text-gray-900">{metrics.pendingApartadosCount}</p>
                  </div>
                  <div 
                    onClick={() => navigate('/apartados?status=PRE-ORDER')}
                    className="p-4 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors group"
                  >
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 group-hover:text-amber-500 transition-colors">Pre-orders</p>
                    <p className="text-2xl font-black text-gray-900">{metrics.pendingPreOrdersCount}</p>
                  </div>
                  <div 
                    onClick={() => navigate('/apartados?status=ALL&expired=true')}
                    className="p-4 bg-red-50 rounded-xl border border-red-100 cursor-pointer hover:bg-red-100 transition-colors group"
                  >
                    <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-1">Vencidos</p>
                    <p className="text-2xl font-black text-red-600 flex items-center gap-2">
                      {metrics.expiredApartadosCount}
                      {metrics.expiredApartadosCount > 0 && <span className="text-sm animate-bounce text-red-500"><AlertCircle className="w-5 h-5" /></span>}
                    </p>
                  </div>
                  <div 
                    onClick={() => navigate('/apartados?status=ALL&warning=true')}
                    className="p-4 bg-orange-50 rounded-xl border border-orange-100 cursor-pointer hover:bg-orange-100 transition-colors group"
                  >
                    <p className="text-xs font-semibold text-orange-400 uppercase tracking-wider mb-1">Por vencer</p>
                    <p className="text-2xl font-black text-orange-500">
                      {metrics.warningApartadosCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alerta de Apartados Vencidos (Compacta si hay widget) */}
            <div className="flex flex-col gap-4">
              {metrics.expiredApartadosCount > 0 && (
                <div 
                  onClick={() => navigate('/apartados')}
                  className="bg-red-50 border border-red-100 p-5 rounded-2xl shadow-sm flex items-center gap-4 cursor-pointer hover:bg-red-100 transition-all group h-full"
                >
                  <div className="bg-red-500 p-3 rounded-xl text-white shadow-lg shadow-red-200 group-hover:scale-110 transition-transform">
                    <AlertCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-red-900 font-black text-lg">Acción Requerida</p>
                    <p className="text-red-600 text-sm leading-tight">
                      Hay {metrics.expiredApartadosCount} apartados con más de 30 días de retraso.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <DashboardTimer />
            </div>
            <p className="text-gray-600 mt-1">Centro operativo — {capitalizedMonth}</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => setIsMetricsModalOpen(true)}
              className="px-4 py-2 flex-1 sm:flex-none bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 shadow-sm"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>Ver Analítica</span>
            </button>
            <button
              onClick={() => setShowCashCutModal(true)}
              className="px-4 py-2 flex-1 sm:flex-none bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Corte del Día</span>
            </button>
            <button
              onClick={refresh}
              disabled={loading}
              className="px-4 py-2 flex-1 sm:flex-none bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{loading ? 'Actualizando...' : 'Actualizar'}</span>
            </button>
          </div>
        </div>

        {/* Métricas principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Notas de Venta del Mes"
            value={metrics?.salesCount || 0}
            icon={SalesIcon}
            color="blue"
            loading={loading}
            trend={revenueComparison ? {
              value: revenueComparison.comparison.salesChange,
              isPositive: revenueComparison.comparison.salesGrowth
            } : undefined}
            onClick={() => navigate('/ventas')}
          />
          <MetricCard
            title="Ingresos Totales"
            value={metrics ? formatCurrency(metrics.totalRevenue) : '$0.00'}
            icon={CashIcon}
            color="green"
            loading={loading}
            trend={revenueComparison ? {
              value: revenueComparison.comparison.revenueChange,
              isPositive: revenueComparison.comparison.revenueGrowth
            } : undefined}
          />
          <MetricCard
            title="Productos en Stock"
            value={metrics?.productsInStock || 0}
            icon={BoxIcon}
            color="purple"
            loading={loading}
          />
          <MetricCard
            title="Órdenes Pendientes"
            value={metrics?.pendingTickets || 0}
            icon={ClipboardIcon}
            color="orange"
            loading={loading}
          />
        </div>

        {/* Concentrado Operativo — Destacado */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          {/* Header del concentrado */}
          <div className="px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Concentrado Operativo</h3>
            </div>
            <span className="text-xs text-white/60">{capitalizedMonth}</span>
          </div>
          {/* Cards del concentrado */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-0 divide-x divide-gray-100">
            {concentradoItems.map((item, idx) => (
              <button
                key={idx}
                onClick={item.onClick}
                className={`${item.bg} p-5 text-left transition-all hover:brightness-95 cursor-pointer group relative overflow-hidden border-b-4 ${item.border} ${idx === 4 ? 'col-span-2 md:col-span-1' : ''}`}
              >
                {/* Watermark icon */}
                <div className={`absolute right-0 top-0 w-20 h-20 ${item.iconWatermark} opacity-40`}>
                  {item.iconLarge}
                </div>
                <div className="relative">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className={item.iconColor}>{item.icon}</span>
                    <p className={`text-xs font-bold ${item.textColor} uppercase tracking-wide`}>{item.label}</p>
                  </div>
                  {loading ? (
                    <div className="h-9 w-28 bg-gray-200 animate-pulse rounded mt-1" />
                  ) : (
                    <p className={`text-3xl font-black ${item.textBold} tracking-tight`}>
                      {formatCurrency(item.value)}
                    </p>
                  )}
                  <p className="text-[11px] text-gray-400 mt-2 group-hover:text-gray-600 transition-colors font-medium">
                    {item.sublabel} →
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tabla de métricas operativas estilo Excel */}
        <OperationalExcelTable />

        {/* Tabla de ventas recientes */}
        <RecentSalesTable sales={recentSales} loading={loading} />

        {/* Rankings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopPerformersRanking
            title="Top Vendedoras del Mes"
            performers={topVendedoras.map(v => ({
              id: v.usuario_id,
              nombre: v.nombre || 'Sin nombre',
              total_ventas: v.total_ventas,
              comision: v.comision_total
            }))}
            type="vendedoras"
            loading={rankingLoading}
            onExpand={() => setIsMetricsModalOpen(true)}
          />
          <TopPerformersRanking
            title="Top Modistas del Mes"
            performers={topModistas.map(m => ({
              id: m.usuario_id,
              nombre: m.nombre || 'Sin nombre',
              total_tickets: m.total_tickets,
              comision: m.comision_total
            }))}
            type="modistas"
            loading={rankingLoading}
            onExpand={() => setIsMetricsModalOpen(true)}
          />
        </div>

        {/* Footer informativo */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900">Datos en tiempo real</h4>
              <p className="text-sm text-blue-800 mt-1">
                Todas las métricas se actualizan automáticamente.
                Los datos están protegidos con autenticación Firebase y encriptación SSL.
              </p>
            </div>
          </div>
        </div>
      </div>

      <GlobalMetricsModal isOpen={isMetricsModalOpen} onClose={() => setIsMetricsModalOpen(false)} />
      <CashCutModal isOpen={showCashCutModal} onClose={() => setShowCashCutModal(false)} />
    </Layout>
  );
};

export default DashboardPage;
